import api from "app/lib/api";
import endpoints from "static/endpoints";
import { BASE_URL } from "static/constants";
import { interpolate } from "utils/string";
import { formatDateRange } from "utils/date";

const task_field_mappings = {
  created_at: "createdAt",
  account: "account",
  description: "description",
  patientName: "name",
  status: "status",
  date: "createdAt",
  created_by: "createdBy",
};

export const getTasks = async (selectedTab, query) => {
  const {
    pagination: { pageSize, pageIndex: page } = {},
    filters,
    sorting,
    search,
    approvalStatus,
  } = query;

  let url = `${BASE_URL}`;
  if (selectedTab === "automated") {
    url += endpoints.task.getAutomatedSchedulingTasks;
  } else if (selectedTab === "archived") {
    url += endpoints.task.getArchivedTasks;
  } else if (selectedTab === "audited") {
    url += endpoints.task.getAuditedTasks;
  } else if (selectedTab === "audited-archived") {
    url += endpoints.task.getAuditedArchivedTasks;
  } else if (selectedTab === "pending-approvals") {
    url += endpoints.task.getPendingApprovals;
  } else url += endpoints.task.getActiveTasks;

  url += "?limit=" + pageSize;
  url += "&page=" + (page + 1);

  if (filters && filters.length > 0) {
    let filterQuery = [];
    filters.forEach((filter) => {
      const { id, value } = filter;

      const filterValue = ["date", "created_at"].includes(id)
        ? formatDateRange(value)
        : value;

      filterQuery.push(`${task_field_mappings[id]}=${filterValue}`);
    });
    url += `&${filterQuery.join("&")}`;
  }

  if (search) {
    url += `&search=${search}`;
  }

  if (approvalStatus) {
    url += `&status=${approvalStatus}`;
  }

  if (sorting?.length > 0) {
    const sortingQueries = sorting
      ?.map((sort) => {
        const { id, desc } = sort;
        const direction = desc ? "desc" : "asc";

        return `sortBy=${id}&order=${direction}&`;
      })
      ?.filter(Boolean)
      ?.join("");

    url += sortingQueries;
  }

  const { data } = await api.get(url);
  const { tasks, requests, page: currentPage, itemsCount } = data;

  // Use requests for pending-approvals tab, otherwise use tasks
  const dataToMap =
    selectedTab === "pending-approvals" ? requests || tasks : tasks;

  const mappedTasks = dataToMap.map((task) => {
    const isPendingApprovals = selectedTab === "pending-approvals";
    const obj = {
      id: task._id,
      description: isPendingApprovals
        ? task.note?.description
        : task.description,
      shortDescription: isPendingApprovals
        ? (task.note?.description || "").substr(0, 30)
        : task.description.substr(0, 30),
      status: task.status,
      reportId: task.reportId,
      patientId: task?.patient?._id,
      patientName: task?.patient?.name,
      patientSerialNumber: task?.patient?.serial_number,
      account: task.account,
      created_at: isPendingApprovals ? task.requested_at : task.created_at,
      ...(["audited", "audited-archived"].includes(selectedTab) && {
        report_date: task.report_date,
        created_by: task.created_by,
      }),
      ...(isPendingApprovals && {
        requested_by: task.requested_by,
        reason: task.reason,
        reviewed_by: task?.reviewed_by,
        reviewed_at: task?.reviewed_at,
      }),
      note: task?.note?.note,
      noteAddedAt: task?.note?.addedAt,
      patient: task?.patient,
      notes: task?.notes,
    };
    return obj;
  });

  return {
    tasks: mappedTasks,
    page: currentPage,
    itemsCount: itemsCount,
  };
};

export const saveTask = async ({
  requestType,
  taskId,
  description,
  status,
  account,
  notes,
  patientId,
  patientName,
  patientSerialNumber,
}) => {
  const payload = {
    description,
    status,
    account,
    ...(notes && { notes }),
    ...(patientId && { patientId }),
    ...(patientName && { patientName }),
    ...(patientSerialNumber && { patientSerialNumber }),
  };

  const url =
    requestType === "POST"
      ? `${BASE_URL}${endpoints.task.createTask}`
      : `${BASE_URL}${interpolate(endpoints.task.updateTask, { taskId })}`;

  let response = null;

  if (requestType === "POST") {
    response = await api.post(url, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } else {
    response = await api.put(url, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  return response;
};

export const archiveTasks = async (taskIds) => {
  const url = `${BASE_URL}${endpoints.task.archiveTasks}`;
  const response = await api.put(
    url,
    { taskIds },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response;
};

export const approveOrDeclineStatus = async (requestId, status) => {
  const url = interpolate(endpoints.task.approveOrDeclineStatus, {
    requestId,
    status,
  });

  return await api.patch(url);
};
